import { CreativeType } from '../domain/campaigns.model';

/** Stable Mixpanel event names — keep in sync with dashboards. */
export const MixpanelEvents = {
  LoginSucceeded: 'Login Succeeded',
  Logout: 'Logout',

  PublisherCreated: 'Publisher Created',
  PublisherArchived: 'Publisher Archived',

  CreativeCreated: 'Creative Created',
  CreativeUpdated: 'Creative Updated',
  CreativeStatusChanged: 'Creative Status Changed',

  CampaignCreated: 'Campaign Created',
  LineItemCreated: 'Line Item Created',

  ReportRun: 'Report Run',
  PlacementCreated: 'Placement Created',

  TargetingCreated: 'Targeting Created',

  PartnerCreated: 'Partner Created',
  ConnectionCreated: 'Connection Created',

  MarketplaceSegmentCreated: 'Marketplace Segment Created',

  KeywordPackageCreated: 'Keyword Package Created',

  AppAdded: 'App Added',

  SupplyBundleListViewed: 'Supply Bundle List Viewed',
  SupplyBundleListLoaded: 'Supply Bundle List Loaded',
  SupplyBundleListLoadFailed: 'Supply Bundle List Load Failed',
  SupplyBundleListFiltered: 'Supply Bundle List Filtered',
  SupplyBundleCreateStarted: 'Supply Bundle Create Started',
  SupplyBundleTypeSelected: 'Supply Bundle Type Selected',
  SupplyBundleCreateDialogCancelled: 'Supply Bundle Create Dialog Cancelled',
  SupplyBundleCreated: 'Supply Bundle Created',
  SupplyBundleUpdated: 'Supply Bundle Updated',
  SupplyBundlePublished: 'Supply Bundle Published',
  SupplyBundleUnpublished: 'Supply Bundle Unpublished',
  SupplyBundleDrafted: 'Supply Bundle Drafted',
  SupplyBundleDuplicated: 'Supply Bundle Duplicated',
  SupplyBundleDetailViewed: 'Supply Bundle Detail Viewed',
  SupplyBundleFormCancelled: 'Supply Bundle Form Cancelled',
  SupplyBundleEditClicked: 'Supply Bundle Edit Clicked',

  PacingTabViewed: 'Pacing Tab Viewed',
  PacingSnapshotLoaded: 'Pacing Snapshot Loaded',
  PacingSnapshotLoadFailed: 'Pacing Snapshot Load Failed',
  PacingSnapshotRefreshed: 'Pacing Snapshot Refreshed',
  PacingFiltersApplied: 'Pacing Filters Applied',
  PacingFiltersCleared: 'Pacing Filters Cleared',
  PacingCampaignExpanded: 'Pacing Campaign Expanded',
  PacingCampaignCollapsed: 'Pacing Campaign Collapsed',
  PacingSortChanged: 'Pacing Sort Changed',
  PacingPageChanged: 'Pacing Page Changed',
  PacingCampaignDetailViewed: 'Pacing Campaign Detail Viewed',
  PacingCampaignDetailLoaded: 'Pacing Campaign Detail Loaded',
  PacingCampaignDetailLoadFailed: 'Pacing Campaign Detail Load Failed',
  PacingCampaignDetailRefreshed: 'Pacing Campaign Detail Refreshed',
  PacingCampaignDetailTabChanged: 'Pacing Campaign Detail Tab Changed',
  PacingLineItemDetailViewed: 'Pacing Line Item Detail Viewed',
  PacingLineItemDetailLoaded: 'Pacing Line Item Detail Loaded',
  PacingLineItemDetailLoadFailed: 'Pacing Line Item Detail Load Failed',
  PacingLineItemDetailRefreshed: 'Pacing Line Item Detail Refreshed',
  PacingLineItemDetailTabChanged: 'Pacing Line Item Detail Tab Changed',

  LoginFailed: 'Login Failed',
  PublisherCreateFailed: 'Publisher Create Failed',
  PublisherArchiveFailed: 'Publisher Archive Failed',
  CreativeCreateFailed: 'Creative Create Failed',
  CreativeUpdateFailed: 'Creative Update Failed',
  CreativeStatusChangeFailed: 'Creative Status Change Failed',
  CampaignCreateFailed: 'Campaign Create Failed',
  LineItemCreateFailed: 'Line Item Create Failed',
  ReportRunFailed: 'Report Run Failed',
  PlacementCreateFailed: 'Placement Create Failed',
  TargetingCreateFailed: 'Targeting Create Failed',
  PartnerCreateFailed: 'Partner Create Failed',
  ConnectionCreateFailed: 'Connection Create Failed',
  MarketplaceSegmentCreateFailed: 'Marketplace Segment Create Failed',
  KeywordPackageCreateFailed: 'Keyword Package Create Failed',
  AppAddFailed: 'App Add Failed',

  SupplyBundleCreateFailed: 'Supply Bundle Create Failed',
  SupplyBundleUpdateFailed: 'Supply Bundle Update Failed',
  SupplyBundlePublishFailed: 'Supply Bundle Publish Failed',
  SupplyBundleUnpublishFailed: 'Supply Bundle Unpublish Failed',
  SupplyBundleDraftFailed: 'Supply Bundle Draft Failed',
  SupplyBundleDuplicateFailed: 'Supply Bundle Duplicate Failed',
  SupplyBundleDetailLoadFailed: 'Supply Bundle Detail Load Failed',

  PacingSnapshotRefreshFailed: 'Pacing Snapshot Refresh Failed',
} as const;

/**
 * Normalizes library creative types for reporting (aligned with studio-style labels where applicable).
 */
export function mixpanelCreativeTypeFromLibrary(type: CreativeType | string | undefined): string {
  if (type == null || type === '') {
    return 'UNKNOWN';
  }
  const t = String(type);
  if (t === CreativeType.MEDIAFILE || t === 'MEDIAFILE') {
    return 'CTV';
  }
  if (t === CreativeType.VAST || t === 'VAST') {
    return 'VAST';
  }
  if (t === CreativeType.DISPLAY || t === 'DISPLAY') {
    return 'DISPLAY';
  }
  if (t === CreativeType.PAUSE_AD || t === 'PAUSE_AD') {
    return 'PAUSE_AD';
  }
  return t.toUpperCase();
}

/** Studio (DCO) creative type for Mixpanel; same uppercase labels as library where enums overlap. */
export function mixpanelCreativeTypeFromStudio(type: string | undefined | null): string | undefined {
  if (type == null || type === '') {
    return undefined;
  }
  return String(type).toUpperCase();
}
